import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { 
  Loader2, TrendingUp, TrendingDown, Target, 
  AlertTriangle, RefreshCw, ArrowUpRight, Zap, DollarSign,
  BarChart3, Clock, Shield, Rocket, Brain, LineChart,
  CheckCircle, XCircle, Timer, Activity, Flame, Play, Pause, Hash
} from "lucide-react";
import AnalysisSections from "../components/AnalysisSections";

import { API } from "../config/api";

// Rebalancing interval: 150 seconds (2.5 minutes)
const REBALANCE_INTERVAL_MS = 150000;
const REBALANCE_INTERVAL_SEC = 150;

const OptionsGeneratorPage = () => {
  const [contracts, setContracts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [capitalToDeploy, setCapitalToDeploy] = useState("20000");
  const [targetReturn, setTargetReturn] = useState("5");
  const [riskLevel, setRiskLevel] = useState("aggressive");
  const [includeCalls, setIncludeCalls] = useState(true);
  const [includePuts, setIncludePuts] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [countdown, setCountdown] = useState(REBALANCE_INTERVAL_SEC);
  const [lastRefresh, setLastRefresh] = useState(null);
  const intervalRef = useRef(null);
  const countdownRef = useRef(null);

  const generateContracts = useCallback(async () => {
    // Derive option type filter from checkboxes
    let optionType = "both";
    if (includeCalls && !includePuts) optionType = "call";
    else if (!includeCalls && includePuts) optionType = "put";
    else if (!includeCalls && !includePuts) {
      toast.error("Please select at least one option type (Calls or Puts)");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/options/high-return-generator`, {
        capital_to_deploy: parseFloat(capitalToDeploy),
        target_daily_return: parseFloat(targetReturn),
        risk_level: riskLevel,
        option_type_filter: optionType
      });
      setContracts(response.data);
      setLastRefresh(new Date());
      setCountdown(REBALANCE_INTERVAL_SEC);
      toast.success(`Found ${response.data.contracts?.length || 0} options matching ${targetReturn}% target`);
    } catch (error) {
      console.error("Error generating options:", error);
      toast.error("Failed to generate options. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [capitalToDeploy, targetReturn, riskLevel, includeCalls, includePuts]);

  // Auto-refresh every 150 seconds (2.5 minutes)
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        generateContracts();
      }, REBALANCE_INTERVAL_MS);
      
      countdownRef.current = setInterval(() => {
        setCountdown(prev => prev > 0 ? prev - 1 : REBALANCE_INTERVAL_SEC);
      }, 1000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [autoRefresh, generateContracts]);

  // Removed auto-generate on mount - user must click the button to start

  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getReturnColor = (val) => {
    if (val >= 10) return "text-green-600 bg-green-100";
    if (val >= 5) return "text-emerald-600 bg-emerald-100";
    if (val >= 0) return "text-blue-600 bg-blue-100";
    return "text-red-600 bg-red-100";
  };

  const getRiskBadge = (risk) => {
    const colors = {
      'LOW': 'bg-green-100 text-green-700 border-green-300',
      'MODERATE': 'bg-yellow-100 text-yellow-700 border-yellow-300',
      'HIGH': 'bg-orange-100 text-orange-700 border-orange-300',
      'EXTREME': 'bg-red-100 text-red-700 border-red-300'
    };
    return colors[risk] || colors['HIGH'];
  };

  const getTypeColor = (type) => {
    return type === 'CALL' ? 'bg-green-600' : 'bg-red-600';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <Toaster position="top-right" />
      
      {/* Header */}
      <header className="bg-black/40 backdrop-blur-md border-b border-purple-500/30 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 animate-pulse">
                <Flame className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-white" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                  Options Generator
                </h1>
                <p className="text-xs text-purple-300">High-Return Options • 5%+ Daily Target</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Auto-refresh toggle */}
              <div className="flex items-center gap-2 bg-slate-800/50 px-3 py-2 rounded-lg border border-purple-500/30">
                <Timer className="w-4 h-4 text-purple-400" />
                <span className="text-sm text-purple-300">
                  {autoRefresh ? formatCountdown(countdown) : "Paused"}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setAutoRefresh(!autoRefresh)}
                  className={autoRefresh ? "text-green-400 hover:text-green-300" : "text-slate-400 hover:text-slate-300"}
                >
                  {autoRefresh ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                </Button>
              </div>
              
              <a 
                href="/" 
                className="bg-slate-700 hover:bg-slate-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1" target="_blank" rel="noopener noreferrer">
                <ArrowUpRight className="w-4 h-4" />
                Dashboard
              </a>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Analysis Sections Navigation */}
        <AnalysisSections currentPage="options-generator" />

        {/* Configuration Panel */}
        <Card className="border-orange-500/30 bg-slate-800/50 backdrop-blur-sm">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg text-white">
              <Brain className="w-5 h-5 text-orange-500" />
              Options Strategy Configuration
              <Badge className="ml-2 bg-orange-600 text-white">150s Rebalancing</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="text-xs font-medium text-purple-300 mb-1 block">Capital to Deploy (Premium)</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="number"
                    value={capitalToDeploy}
                    onChange={(e) => setCapitalToDeploy(e.target.value)}
                    className="pl-9 bg-slate-700 border-slate-600 text-white"
                    placeholder="20000"
                  />
                </div>
                <p className="text-[10px] text-slate-400 mt-1">Total premium to invest in options</p>
              </div>
              <div>
                <label className="text-xs font-medium text-purple-300 mb-1 block">Target Daily Return (%)</label>
                <div className="relative">
                  <TrendingUp className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="number"
                    value={targetReturn}
                    onChange={(e) => setTargetReturn(e.target.value)}
                    className="pl-9 bg-slate-700 border-slate-600 text-white"
                    placeholder="5"
                    step="0.5"
                    min="1"
                    max="50"
                  />
                </div>
                <p className="text-[10px] text-slate-400 mt-1">Options matching this return will be found</p>
              </div>
              <div>
                <label className="text-xs font-medium text-purple-300 mb-1 block">Risk Profile</label>
                <select
                  value={riskLevel}
                  onChange={(e) => setRiskLevel(e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-slate-600 bg-slate-700 text-white text-sm"
                >
                  <option value="moderate">Moderate</option>
                  <option value="aggressive">Aggressive</option>
                  <option value="extreme">Extreme (Max Returns)</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-purple-300 mb-1 block">Option Type</label>
                <div className="flex items-center gap-4 h-10 px-3 rounded-md border border-slate-600 bg-slate-700">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="calls-checkbox"
                      checked={includeCalls}
                      onCheckedChange={setIncludeCalls}
                      className="border-green-500 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                    />
                    <label 
                      htmlFor="calls-checkbox" 
                      className="text-sm text-green-400 font-medium cursor-pointer"
                    >
                      Calls
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="puts-checkbox"
                      checked={includePuts}
                      onCheckedChange={setIncludePuts}
                      className="border-red-500 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
                    />
                    <label 
                      htmlFor="puts-checkbox" 
                      className="text-sm text-red-400 font-medium cursor-pointer"
                    >
                      Puts
                    </label>
                  </div>
                </div>
                <p className="text-[10px] text-slate-400 mt-1">Filter by option type</p>
              </div>
            </div>
            
            {/* Generate Button - Full Width */}
            <div className="mt-4">
              <Button 
                onClick={generateContracts}
                disabled={loading || (!includeCalls && !includePuts)}
                className="w-full bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white h-11"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Zap className="w-4 h-4 mr-2" />
                )}
                {loading ? "Scanning Market..." : "Generate Options"}
              </Button>
            </div>
            
            {lastRefresh && (
              <div className="mt-3 text-xs text-slate-400 flex items-center gap-2">
                <Clock className="w-3 h-3" />
                Last updated: {lastRefresh.toLocaleTimeString()}
                {autoRefresh && <Badge variant="outline" className="text-green-400 border-green-500">Auto-refresh ON</Badge>}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Loading State */}
        {loading && !contracts && (
          <Card className="border-purple-500/30 bg-slate-800/50">
            <CardContent className="flex items-center justify-center py-20">
              <div className="text-center">
                <Loader2 className="w-12 h-12 animate-spin text-orange-500 mx-auto mb-4" />
                <p className="text-white font-medium">Scanning options chains...</p>
                <p className="text-purple-300 text-sm">Analyzing momentum, IV, sentiment, and timing</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Options Results */}
        {contracts && (
          <>
            {/* Liquidity Warning Banner */}
            {contracts.has_liquidity_warning && (
              <Card className="border-yellow-500/50 bg-yellow-900/30">
                <CardContent className="p-4 flex items-center gap-3">
                  <AlertTriangle className="w-6 h-6 text-yellow-400 flex-shrink-0" />
                  <div>
                    <p className="text-yellow-300 font-semibold">⚠️ Low Liquidity Warning</p>
                    <p className="text-sm text-yellow-200/80">
                      Markets may be closed or experiencing low trading volume. 
                      {contracts.illiquid_options_count > 0 && ` ${contracts.illiquid_options_count} options shown have limited bid/ask data.`}
                      {' '}Prices may not be executable - verify quotes before trading.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Card className="border-green-500/30 bg-gradient-to-br from-green-900/50 to-emerald-900/50">
                <CardContent className="p-4">
                  <p className="text-xs text-green-300 flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> Avg Expected Return
                  </p>
                  <p className="text-2xl font-bold text-green-400">
                    +{contracts.avg_expected_return}%
                  </p>
                  <p className="text-xs text-green-300">Daily (Target: {targetReturn}%)</p>
                </CardContent>
              </Card>
              <Card className="border-purple-500/30 bg-gradient-to-br from-purple-900/50 to-indigo-900/50">
                <CardContent className="p-4">
                  <p className="text-xs text-purple-300 flex items-center gap-1">
                    <DollarSign className="w-3 h-3" /> Capital Deployed
                  </p>
                  <p className="text-2xl font-bold text-purple-400">
                    ${(contracts.total_premium || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-purple-300">of ${parseFloat(capitalToDeploy).toLocaleString()} target</p>
                </CardContent>
              </Card>
              <Card className="border-blue-500/30 bg-gradient-to-br from-blue-900/50 to-cyan-900/50">
                <CardContent className="p-4">
                  <p className="text-xs text-blue-300 flex items-center gap-1">
                    <Target className="w-3 h-3" /> Potential Profit
                  </p>
                  <p className="text-2xl font-bold text-blue-400">
                    ${(contracts.potential_profit || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-blue-300">If targets hit</p>
                </CardContent>
              </Card>
              <Card className="border-orange-500/30 bg-gradient-to-br from-orange-900/50 to-red-900/50">
                <CardContent className="p-4">
                  <p className="text-xs text-orange-300 flex items-center gap-1">
                    <Hash className="w-3 h-3" /> Total Contracts
                  </p>
                  <p className="text-2xl font-bold text-orange-400">
                    {contracts.total_contracts_to_buy || 0}
                  </p>
                  <p className="text-xs text-orange-300">{contracts.contracts?.length || 0} positions</p>
                </CardContent>
              </Card>
              <Card className="border-yellow-500/30 bg-gradient-to-br from-yellow-900/50 to-amber-900/50">
                <CardContent className="p-4">
                  <p className="text-xs text-yellow-300 flex items-center gap-1">
                    <Shield className="w-3 h-3" /> Win Rate Est.
                  </p>
                  <p className="text-2xl font-bold text-yellow-400">
                    {contracts.estimated_win_rate}%
                  </p>
                  <p className="text-xs text-yellow-300">Historical</p>
                </CardContent>
              </Card>
            </div>

            {/* AI Strategy Summary */}
            <Card className="border-orange-500/30 bg-gradient-to-br from-slate-800/80 to-orange-900/30">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg text-white">
                  <Brain className="w-5 h-5 text-orange-500" />
                  AI Strategy Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-line">
                  {contracts.strategy_summary}
                </p>
              </CardContent>
            </Card>

            {/* Options Contracts Table */}
            <Card className="border-slate-600 bg-slate-800/50">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg text-white">
                  <Flame className="w-5 h-5 text-orange-500" />
                  Recommended Options Contracts
                  <Badge variant="outline" className="ml-2 text-orange-400 border-orange-500">
                    {contracts.total_contracts_to_buy || 0} contracts to buy
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-700/50 text-slate-300">
                        <th className="text-left py-3 px-3 font-semibold">Contract</th>
                        <th className="text-center py-3 px-3 font-semibold">Type</th>
                        <th className="text-center py-3 px-3 font-semibold">Strike</th>
                        <th className="text-center py-3 px-3 font-semibold">Expiry</th>
                        <th className="text-center py-3 px-3 font-semibold"># to Buy</th>
                        <th className="text-center py-3 px-3 font-semibold">Premium</th>
                        <th className="text-center py-3 px-3 font-semibold">Opt Chg%</th>
                        <th className="text-center py-3 px-3 font-semibold">Total Cost</th>
                        <th className="text-center py-3 px-3 font-semibold">Delta</th>
                        <th className="text-center py-3 px-3 font-semibold">Expected Return</th>
                        <th className="text-center py-3 px-3 font-semibold hidden lg:table-cell">Signal</th>
                        <th className="text-left py-3 px-3 font-semibold hidden xl:table-cell">Rationale</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contracts.contracts?.map((contract, idx) => (
                        <tr 
                          key={idx}
                          className={`border-b border-slate-700 hover:bg-slate-700/30 ${
                            idx < 3 ? 'bg-orange-900/20' : ''
                          }`}
                        >
                          <td className="py-3 px-3">
                            <div className="flex items-center gap-2">
                              <a 
                                href={`/stock?ticker=${contract.underlying}`}
                                className="font-bold text-orange-400 hover:text-orange-300 hover:underline" target="_blank" rel="noopener noreferrer">
                                {contract.underlying}
                              </a>
                              {idx < 3 && (
                                <Badge className="bg-orange-600 text-white text-[10px]">TOP</Badge>
                              )}
                            </div>
                            <p className="text-xs text-slate-400">{contract.company_name}</p>
                          </td>
                          <td className="py-3 px-3 text-center">
                            <Badge className={`${getTypeColor(contract.option_type)} text-white`}>
                              {contract.option_type}
                            </Badge>
                          </td>
                          <td className="py-3 px-3 text-center text-white font-medium">
                            ${contract.strike}
                          </td>
                          <td className="py-3 px-3 text-center">
                            <span className="text-purple-300">{contract.expiry}</span>
                            <p className="text-xs text-slate-500">{contract.days_to_expiry}d</p>
                          </td>
                          <td className="py-3 px-3 text-center">
                            <span className="text-xl font-bold text-yellow-400">{contract.contracts_to_buy}</span>
                          </td>
                          <td className="py-3 px-3 text-center text-white">
                            ${contract.premium?.toFixed?.(2) || "0.00"}
                          </td>
                          <td className="py-3 px-3 text-center">
                            <span className={`font-semibold ${
                              contract.option_pct_change > 0 ? 'text-green-400' : 
                              contract.option_pct_change < 0 ? 'text-red-400' : 
                              'text-slate-400'
                            }`}>
                              {contract.option_pct_change > 0 ? '+' : ''}{contract.option_pct_change?.toFixed(1) || '0.0'}%
                            </span>
                            {contract.option_pct_change > 0 && (
                              <span className="block text-[10px] text-green-300">🔥 Hot</span>
                            )}
                          </td>
                          <td className="py-3 px-3 text-center text-green-400 font-semibold">
                            ${contract.total_cost?.toLocaleString()}
                          </td>
                          <td className="py-3 px-3 text-center text-cyan-400">
                            {contract.delta?.toFixed?.(2) || "0.00"}
                          </td>
                          <td className="py-3 px-3 text-center">
                            <Badge className={getReturnColor(contract.expected_return)}>
                              +{contract.expected_return}%
                            </Badge>
                          </td>
                          <td className="py-3 px-3 text-center hidden lg:table-cell">
                            <Badge variant="outline" className={`text-xs ${
                              contract.signal === 'STRONG_BUY' ? 'text-green-400 border-green-500' :
                              contract.signal === 'BUY' ? 'text-emerald-400 border-emerald-500' :
                              contract.signal === 'CAUTION' ? 'text-orange-400 border-orange-500' :
                              contract.signal === 'LOW_LIQUIDITY' ? 'text-red-400 border-red-500' :
                              'text-yellow-400 border-yellow-500'
                            }`}>
                              {contract.signal}
                            </Badge>
                            {contract.is_illiquid && (
                              <span className="block text-[10px] text-orange-400 mt-0.5">⚠️ Illiquid</span>
                            )}
                          </td>
                          <td className="py-3 px-3 hidden xl:table-cell">
                            <span className="text-xs text-slate-400 block max-w-[200px]">
                              {contract.rationale}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Rebalancing Schedule */}
            <Card className="border-purple-500/30 bg-slate-800/50">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg text-white">
                  <Timer className="w-5 h-5 text-purple-500" />
                  150-Second Rebalancing Protocol
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-purple-900/30 rounded-lg border border-purple-500/30">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-purple-400" />
                      <span className="font-semibold text-purple-300">Every 150 Seconds</span>
                    </div>
                    <p className="text-xs text-slate-400">
                      Re-scan for momentum shifts, IV changes, and new entry points.
                    </p>
                  </div>
                  <div className="p-4 bg-green-900/30 rounded-lg border border-green-500/30">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="font-semibold text-green-300">Take Profits</span>
                    </div>
                    <p className="text-xs text-slate-400">
                      Close positions hitting target return. Lock in profits aggressively.
                    </p>
                  </div>
                  <div className="p-4 bg-red-900/30 rounded-lg border border-red-500/30">
                    <div className="flex items-center gap-2 mb-2">
                      <XCircle className="w-4 h-4 text-red-400" />
                      <span className="font-semibold text-red-300">Cut Losses</span>
                    </div>
                    <p className="text-xs text-slate-400">
                      Exit immediately if down 20%+. Preserve capital for next play.
                    </p>
                  </div>
                  <div className="p-4 bg-orange-900/30 rounded-lg border border-orange-500/30">
                    <div className="flex items-center gap-2 mb-2">
                      <Rocket className="w-4 h-4 text-orange-400" />
                      <span className="font-semibold text-orange-300">Rotate</span>
                    </div>
                    <p className="text-xs text-slate-400">
                      Redeploy capital from closed positions into new high-conviction plays.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Risk Warning */}
            <Card className="border-red-500/50 bg-red-900/20">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg text-red-400">
                  <AlertTriangle className="w-5 h-5" />
                  Critical Risk Factors
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {contracts.risk_factors?.map((factor, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-3 bg-red-900/30 rounded-lg border border-red-500/30">
                      <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-slate-300">{factor}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Disclaimer */}
            <div className="p-4 bg-slate-800/50 rounded-lg border border-red-500/30">
              <p className="text-xs text-red-300 text-center">
                <strong>⚠️ EXTREME RISK WARNING:</strong> Options trading involves substantial risk of loss. 
                Targeting 5%+ daily returns is extremely aggressive and most traders will lose money. 
                These are AI-generated suggestions for educational purposes only. 
                Never trade with money you cannot afford to lose. Past performance does not guarantee future results.
              </p>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default OptionsGeneratorPage;
